import React, { useEffect, useState } from 'react';

import { Button } from 'components/ui/button';
import { brokerRemindedAt, brokerRemindedData } from '../../actions';
import { connect } from 'react-redux';
import { get } from 'helpers/api';
import { diffHours, parseDate } from 'helpers/date_utils';
import PropTypes from 'prop-types';
import { getProfileData } from '../../helpers/preview';
import LoadingSpinner from 'components/ui/loading_spinner';
import { showSnackbar } from 'components/ui/snackbar/helper';
import { fetchTranslation } from '../../../../helpers/i18n';

const REMIND_BROKER_STATES = {
  DEFAULT: 'default',
  SENDING: 'sending',
  SENT: 'sent'
};

function RemindBroker({
  brokerRemindedAt,
  brokerRemindedData,
  lastRequestedAccess,
  readonly = false,
  remindBrokerUrl,
  vaultPath
}) {
  const [remindBrokerState, setRemindBrokerState] = useState(REMIND_BROKER_STATES.DEFAULT);
  const [checkedLastRequestedAccess, setCheckedLastRequestedAccess] = useState(false);

  const canRemindBroker = checkedLastRequestedAccess && remindBrokerUrl && (!lastRequestedAccess ||
    (lastRequestedAccess && diffHours(new Date(), parseDate(lastRequestedAccess)) >= 24));

  const validLastRequestedAccess = !!lastRequestedAccess && lastRequestedAccess !== 0;

  function showRemindBroker() {
    return (canRemindBroker && remindBrokerState === REMIND_BROKER_STATES.DEFAULT) ||
      remindBrokerState === REMIND_BROKER_STATES.SENDING || remindBrokerState === REMIND_BROKER_STATES.SENT;
  }

  function params() {
    return { readonly, previewProfile: getProfileData() };
  }

  async function sendBrokerReminder() {
    setRemindBrokerState(REMIND_BROKER_STATES.SENDING);
    try {
      await get(remindBrokerUrl, { params: params() });
      setRemindBrokerState(REMIND_BROKER_STATES.SENT);
      brokerRemindedAt(Date.now());
      showSnackbar({ text: fetchTranslation('successfully_sent_request'), variant: 'success' });
    } catch {
      setRemindBrokerState(REMIND_BROKER_STATES.DEFAULT);
    }
  }

  useEffect(() => {
    if (lastRequestedAccess) {
      setCheckedLastRequestedAccess(true);
    } else {
      get(`${vaultPath}/broker_remind_data`, { params: params() }).then((data) => {
        brokerRemindedData(data.lastRequestedAccess, data.remindBrokerPath);
        setCheckedLastRequestedAccess(true);
      });
    }
  }, []);

  useEffect(() => {
    if (checkedLastRequestedAccess && !validLastRequestedAccess) {
      // initial request should be sent automatically
      sendBrokerReminder();
    }
  }, [checkedLastRequestedAccess, validLastRequestedAccess]);

  function formatDate(dateInMs) {
    if (!validLastRequestedAccess) return;

    const date = new Date(dateInMs);
    const datePart = date.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric'
    });
    const timePart = date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });

    return `${datePart} ${timePart}`;
  }

  if (!checkedLastRequestedAccess) {
    return (
      <div className="text-center">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="text-center mt-6">
      <RemindBrokerButton
        readonly={readonly}
        remindBrokerState={remindBrokerState}
        showRemindBroker={showRemindBroker()}
        validLastRequestedAccess={validLastRequestedAccess}
        onRemindBroker={sendBrokerReminder}
      />
      {validLastRequestedAccess ? (
        <div className="text-muted mt-3"><small>Sent {formatDate(lastRequestedAccess)}</small></div>
      ) : null}
    </div>
  );
}

function RemindBrokerButton({
  readonly,
  remindBrokerState,
  showRemindBroker,
  validLastRequestedAccess,
  onRemindBroker
}) {
  if (!showRemindBroker) {
    return null;
  }

  switch (remindBrokerState) {
  case REMIND_BROKER_STATES.DEFAULT: {
    const buttonText = validLastRequestedAccess ? fetchTranslation('remind_broker') : fetchTranslation('send_request');
    return <Button className="primary" disabled={readonly} label={buttonText} onClick={onRemindBroker} />;
  }
  case REMIND_BROKER_STATES.SENDING: {
    const buttonText = validLastRequestedAccess ?
      `${fetchTranslation('reminding_broker')}...` :
      `${fetchTranslation('sending_request')}...`;
    return (
      <Button className="primary" disabled>
        <LoadingSpinner flex size="sm" skipTopBuffer />
        {buttonText}
      </Button>
    );
  }
  case REMIND_BROKER_STATES.SENT: {
    const buttonText = fetchTranslation('message_has_been_sent');
    return <Button className="primary" disabled label={buttonText} />;
  }
  }
}

RemindBrokerButton.propTypes = {
  readonly: PropTypes.bool.isRequired,
  remindBrokerState: PropTypes.string.isRequired,
  showRemindBroker: PropTypes.bool.isRequired,
  validLastRequestedAccess: PropTypes.bool.isRequired,
  onRemindBroker: PropTypes.func.isRequired
};

RemindBroker.propTypes = {
  brokerRemindedAt: PropTypes.func,
  brokerRemindedData: PropTypes.func,
  lastRequestedAccess: PropTypes.number,
  readonly: PropTypes.bool,
  remindBrokerUrl: PropTypes.string,
  vaultPath: PropTypes.string
};

export const mapStateToProps = ({ ui: { lastRequested }, vault: { path } }) => {
  return {
    lastRequestedAccess: lastRequested.timestamp,
    remindBrokerUrl: lastRequested.url,
    vaultPath: path
  };
};
const mapDispatchToProps = { brokerRemindedAt, brokerRemindedData };
export default connect(mapStateToProps, mapDispatchToProps)(RemindBroker);
